/**
 * ResultadosLayout · v8.5.58 · piel — título "Resultados" font-display 44px +
 * pestañas subrayadas + selector de período a la derecha + Exportar. Deja de
 * usar ZoneNav (nav vertical de 200px compartida con Cuenta/Trabajo/Plan) por
 * el mismo motivo que PlanLayout.jsx: el spec pide un encabezado con forma
 * que ZoneNav no tiene, y las otras 2 zonas (Cuenta/Trabajo) no pidieron
 * cambiar. ZoneNav.jsx queda intacto para ellas.
 *
 * El período (`?periodo=YYYY-MM`) se expone a las 2 pestañas via <Outlet
 * context>, mismo patrón que PlanLayout — pero solo Reportes.jsx (pestaña
 * "Resumen") lo consume hoy. Galería no filtra por fecha todavía.
 *
 * v8.6.22 · Fase 24 — Coberturas se mudó a la zona "Plan"
 * (PlanLayout.jsx): no es contenido retrospectivo, es algo que el
 * cliente necesita ver venir (fecha, qué preparar) — no encajaba bien
 * atado al período mensual de "Resultados". Redirect en App.jsx.
 */
import { useMemo } from "react";
import { NavLink, Outlet, useLocation, useSearchParams } from "react-router-dom";
import ExportMenu from "./ExportMenu.jsx";

const TABS = [
  { to: "/reportes",           label: "Resumen",
    isActive: (p) => p === "/reportes" || (p.startsWith("/reportes/") && !p.startsWith("/reportes/galeria")) },
  { to: "/reportes/galeria",   label: "Galería",
    isActive: (p) => p.startsWith("/reportes/galeria") },
];

function mesActual() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function ResultadosLayout() {
  const { pathname } = useLocation();
  const [params, setParams] = useSearchParams();
  const periodo = params.get("periodo") || mesActual();

  const setPeriodo = (v) => {
    setParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("periodo", v);
      return next;
    }, { replace: true });
  };

  const items = useMemo(() => TABS.map(t => ({ ...t, active: t.isActive(pathname) })), [pathname]);
  // v8.6.22 · Fase 26 — el selector de período no afecta a Galería (ver
  // docblock arriba) — antes quedaba visible igual, activamente confuso:
  // un cliente lo cambiaba pensando que filtraba la galería y no pasaba
  // nada. Se muestra solo en la pestaña que realmente lo consume.
  const enResumen = items.find(t => t.to === "/reportes")?.active ?? true;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <h1 className="h1-portal text-[var(--ink)]">Resultados</h1>
        <div className="flex items-center gap-3">
          {enResumen && (
            <input type="month" value={periodo} onChange={e => setPeriodo(e.target.value)}
                   className="px-4 h-10 bg-[var(--surface)] rounded-full text-sm font-semibold text-[var(--ink-2)] focus:outline-none focus:border-[var(--accent)]"/>
          )}
          <ExportMenu tipo="reportes"/>
        </div>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide [&>*]:shrink-0 [&>*]:whitespace-nowrap">
        {items.map(t => (
          // Solo "Resumen" lee `periodo` — a Galería/Coberturas no hace falta
          // arrastrárselo (Coberturas tiene su propio filtro de fecha).
          <NavLink key={t.to} to={t.to === "/reportes" ? `${t.to}?periodo=${periodo}` : t.to}
                   className={() => `inline-flex items-center h-10 px-[18px] rounded-full text-[15px] transition-colors ${
                     t.active ? "bg-[var(--ink)] text-[var(--surface)] font-extrabold" : "bg-[var(--surface)] text-[var(--ink-3)] font-semibold hover:bg-[var(--line)]"
                   }`}>
            {t.label}
          </NavLink>
        ))}
      </div>

      <Outlet context={{ periodo, setPeriodo }}/>
    </div>
  );
}
